import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../../config/firebase-config";
import {
  collection,
  query,
  where,
  getDocs,
  limit,
  doc,
  getDoc
} from "firebase/firestore";
import SongRequestForm from "../../components/forms/SongRequestForm.jsx";
import { RequestASongLogo } from '../../components/common/RequestASongLogo.jsx';

// 🔒 SECURE LOGGER - Prevents sensitive data exposure
const SENSITIVE_FIELDS = [
  'phoneNumber', 'mobileNumber', 'email', 'phone', 'mobile', 'realName', 
  'fullName', 'legalName', 'emailAddress', 'address', 'street', 'city', 
  'postalCode', 'zipCode', 'ssn', 'socialSecurityNumber', 'bankAccount',
  'stripeAccountId', 'paymentMethod', 'creditCard', 'accountNumber'
];

const sanitizeData = (data) => {
  if (!data || typeof data !== 'object') return data;
  
  if (Array.isArray(data)) {
    return data.map(item => sanitizeData(item));
  }
  
  const sanitized = {};
  for (const [key, value] of Object.entries(data)) {
    if (SENSITIVE_FIELDS.includes(key)) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof value === 'string') {
      // Check for email patterns
      if (value.includes('@') && value.includes('.') && value.indexOf('@') < value.lastIndexOf('.')) {
        sanitized[key] = '[EMAIL_REDACTED]';
      }
      // Check for phone patterns
      else if (/(\+?\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/.test(value)) {
        sanitized[key] = '[PHONE_REDACTED]';
      }
      // Check for suspicious number patterns
      else if (/\d{8,}/.test(value.replace(/\s/g, ''))) {
        sanitized[key] = '[NUMBER_REDACTED]';
      }
      else {
        sanitized[key] = value;
      }
    } else if (typeof value === 'object') {
      sanitized[key] = sanitizeData(value);
    } else {
      sanitized[key] = value;
    }
  }
  return sanitized;
};

const secureLogger = {
  log: (...args) => {
    if (process.env.NODE_ENV === 'production') return;
    const sanitizedArgs = args.map(arg => sanitizeData(arg));
    console.log(...sanitizedArgs);
  },
  
  error: (...args) => {
    // Always allow errors, but sanitize them
    const sanitizedArgs = args.map(arg => sanitizeData(arg));
    console.error(...sanitizedArgs);
  },
  
  warn: (...args) => {
    if (process.env.NODE_ENV === 'production') return;
    const sanitizedArgs = args.map(arg => sanitizeData(arg));
    console.warn(...sanitizedArgs);
  },
  
  info: (...args) => {
    if (process.env.NODE_ENV === 'production') return;
    const sanitizedArgs = args.map(arg => sanitizeData(arg));
    console.info(...sanitizedArgs);
  }
};

// Utility functions
const formatDate = (date) => {
  const now = new Date();
  const diffTime = Math.abs(now - date);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.ceil(diffDays / 30)} months ago`;
  return `${Math.ceil(diffDays / 365)} years ago`;
};

const formatRating = (rating) => '⭐'.repeat(rating) + '☆'.repeat(5 - rating);
const getRatingStars = (rating) => "⭐".repeat(Math.round(rating));

// Shared styles
const useResponsiveStyles = () => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
  
  return {
    isMobile,
    cardStyle: {
      backgroundColor: '#1F2937',
      border: '8px solid #7C3AED',
      borderRadius: '24px',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      overflow: 'hidden'
    },
    buttonStyle: (bgColor, borderColor, hoverBg = null) => ({
      backgroundColor: bgColor,
      color: 'white',
      border: `4px solid ${borderColor}`,
      borderRadius: '12px',
      padding: isMobile ? '12px 20px' : '16px 24px',
      fontWeight: 'bold',
      cursor: 'pointer',
      fontSize: isMobile ? '14px' : '16px',
      transition: 'all 0.3s ease'
    })
  };
};

// Combined Modal Component - handles both request and rating
function CombinedModal({ 
  type, // 'request' | 'rating' | 'cancel-confirm' | 'success'
  isOpen, 
  onClose, 
  data = {} 
}) {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tipAmount, setTipAmount] = useState(0);
  const maxTipAmount = 20;
  const { isMobile, buttonStyle } = useResponsiveStyles();

  const submitRating = async () => {
    if (rating === 0) {
      alert("Please select a rating");
      return;
    }

    setIsSubmitting(true);
    try {
      const functions = getFunctions();
      const submitRatingAndCapture = httpsCallable(functions, 'submitRatingAndCapture');
      
      const result = await submitRatingAndCapture({
        paymentIntentId: data.paymentIntentId,
        rating,
        comment: comment.trim(),
        tipAmount: tipAmount,
        songId: data.song?.id,
        songName: data.song?.name,
        songArtist: data.song?.artist,
        musicianId: data.song?.musicianId || "unknown"
      });

      if (result.data.success) {
        data.onSuccess?.();
      }
    } catch (error) {
      secureLogger.error("Error submitting rating:", error);
      alert("Failed to submit rating. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const cancelPayment = async () => {
    setIsSubmitting(true);
    try {
      const functions = getFunctions();
      const cancelPaymentFunc = httpsCallable(functions, 'cancelPayment');
      
      const result = await cancelPaymentFunc({
        paymentIntentId: data.paymentIntentId,
        reason: "Not satisfied with performance",
        songId: data.song?.id,
        songName: data.song?.name,
        musicianId: data.song?.musicianId || "unknown"
      });

      if (result.data.success) {
        // Clear the pending rating from localStorage
        const allPending = JSON.parse(localStorage.getItem('pendingRatings') || '[]');
        const filteredPending = allPending.filter(r => 
          r.paymentIntentId !== data.paymentIntentId
        );
        localStorage.setItem('pendingRatings', JSON.stringify(filteredPending));
        
        console.log('✅ Removed canceled payment from localStorage:', data.paymentIntentId);
        console.log('📋 Remaining pending ratings:', filteredPending);
        
        // Call the onCancel callback if provided to update parent state
        if (data.onCancel) {
          data.onCancel(data.paymentIntentId);
        }
        
        alert("Payment has been cancelled. No charge has been made.");
        onClose();
      }
    } catch (error) {
      secureLogger.error("Error cancelling payment:", error);
      alert("Failed to cancel payment. Please try again or contact support.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const modalBaseStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    overflowY: 'auto'
  };

  const modalContentStyle = {
    backgroundColor: '#1F2937',
    borderRadius: '24px',
    padding: '32px',
    maxWidth: '500px',
    margin: '20px',
    maxHeight: '90vh',
    overflowY: 'auto'
  };

  // Success Modal
  if (type === 'success') {
    return (
      <div style={modalBaseStyle}>
        <div style={{
          ...modalContentStyle,
          backgroundColor: '#065F46',
          border: '8px solid #10B981',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '64px', marginBottom: '24px' }}>🎉</div>
          <h2 style={{ color: 'white', fontSize: '24px', fontWeight: 'bold', margin: '0 0 16px 0' }}>
            Thank You!
          </h2>
          <p style={{ color: '#A7F3D0', fontSize: '18px', lineHeight: '1.5', margin: 0 }}>
            Your rating has been submitted and payment has been processed. The musician has been notified!
          </p>
        </div>
      </div>
    );
  }

  // Cancel Confirmation Modal
  if (type === 'cancel-confirm') {
    return (
      <div style={modalBaseStyle}>
        <div style={{
          ...modalContentStyle,
          backgroundColor: '#7F1D1D',
          border: '8px solid #DC2626',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '24px' }}>⚠️</div>
          <h2 style={{ color: 'white', fontSize: '24px', fontWeight: 'bold', margin: '0 0 16px 0' }}>
            Cancel Payment?
          </h2>
          <p style={{ color: '#FCA5A5', fontSize: '16px', lineHeight: '1.5', margin: '0 0 24px 0' }}>
            This will cancel your payment and no charge will be made. Are you sure?
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <button 
              onClick={onClose} 
              disabled={isSubmitting} 
              style={{
                ...buttonStyle('#374151', '#6B7280'),
                opacity: isSubmitting ? 0.5 : 1,
                cursor: isSubmitting ? 'not-allowed' : 'pointer'
              }}
            >
              Keep Payment
            </button>
            <button 
              onClick={cancelPayment} 
              disabled={isSubmitting} 
              style={{
                ...buttonStyle('#DC2626', '#B91C1C'),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                opacity: isSubmitting ? 0.7 : 1
              }}
            >
              {isSubmitting && (
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite'
                }}></div>
              )}
              {isSubmitting ? 'Cancelling...' : 'Yes, Cancel'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Song Request Modal
  if (type === 'request') {
    return (
      <div style={modalBaseStyle}>
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)'
          }}
          onClick={onClose}
        />
        
        <div style={{ position: 'relative', width: '100%', maxWidth: '500px', margin: '16px' }}>
          <div style={{
            backgroundColor: '#1F2937',
            border: '8px solid #EC4899',
            borderRadius: '24px',
            overflow: 'hidden',
            height: '700px',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{ 
              padding: '30px', 
              borderBottom: '4px solid #DB2777', 
              backgroundColor: '#BE185D',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0
            }}>
              <div>
                <h2 style={{ fontSize: '28px', fontWeight: 'bold', color: 'white', margin: '0 0 8px 0' }}>
                  Request: {data.song?.name}
                </h2>
                <p style={{ color: '#FBCFE8', fontSize: '18px', margin: 0 }}>
                  by {data.song?.artist} • ${data.song?.price?.toFixed(2)}
                </p>
              </div>
              <button onClick={onClose} style={{
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                cursor: 'pointer',
                fontSize: '20px'
              }}>
                ✕
              </button>
            </div>
            
            <div style={{ padding: '40px 30px', flex: 1, overflowY: 'auto' }}>
              <SongRequestForm 
                musicianId={data.musicianId} 
                musicianName={data.musicianName}
                preSelectedSong={data.song}
                onPaymentAuthorized={data.onPaymentAuthorized}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Rating Modal
  if (type === 'rating') {
    return (
      <div style={modalBaseStyle}>
        <div style={{
          ...modalContentStyle,
          border: '8px solid #FBBF24'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎵</div>
            <h2 style={{ color: 'white', fontSize: '28px', fontWeight: 'bold', margin: '0 0 8px 0' }}>
              Rate the Performance
            </h2>
            <p style={{ color: '#D1D5DB', fontSize: '18px', margin: 0 }}>
              "{data.song?.name}" by {data.song?.artist}
            </p>
            <p style={{ color: '#FBBF24', fontSize: '16px', fontWeight: 'bold', margin: '8px 0 0 0' }}>
              Performed by {data.musicianName} • ${data.amount?.toFixed(2)}
            </p>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', color: 'white', fontSize: '18px', fontWeight: 'bold', marginBottom: '16px' }}>
              How was the performance?
            </label>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '16px' }}>
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  onClick={() => setRating(star)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '40px',
                    cursor: 'pointer',
                    color: star <= (hoveredRating || rating) ? '#FBBF24' : '#6B7280',
                    transition: 'color 0.2s ease'
                  }}
                >
                  {star <= (hoveredRating || rating) ? '★' : '☆'}
                </button>
              ))}
            </div>
            <div style={{ textAlign: 'center' }}>
              <span style={{ color: '#D1D5DB', fontSize: '14px' }}>
                {rating === 0 ? 'Click to rate' : 
                 ['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][rating]}
              </span>
            </div>
          </div>

          {/* Tip Selection */}
          <div style={{ 
            marginBottom: '24px',
            backgroundColor: '#1E293B',
            border: '2px solid #475569',
            borderRadius: '12px',
            padding: '20px'
          }}>
            <h3 style={{ 
              color: '#E2E8F0', 
              fontSize: '16px', 
              fontWeight: 'bold',
              marginBottom: '16px',
              margin: '0 0 16px 0',
              textAlign: 'center'
            }}>
              💝 Add a Tip (Optional)
            </h3>
            
            {/* Total Amount Display */}
            <div style={{
              backgroundColor: '#065F46',
              border: '2px solid #10B981',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '16px',
              textAlign: 'center'
            }}>
              <div style={{ color: '#6EE7B7', fontSize: '14px', marginBottom: '4px' }}>
                Total to Pay:
              </div>
              <div style={{ color: '#ECFDF5', fontSize: '20px', fontWeight: 'bold' }}>
                ${((data.amount || 0) + tipAmount).toFixed(2)}
              </div>
              <div style={{ color: '#A7F3D0', fontSize: '12px', marginTop: '4px' }}>
                Base: ${(data.amount || 0).toFixed(2)} {tipAmount > 0 ? `+ Tip: $${tipAmount.toFixed(2)}` : ''}
              </div>
            </div>
            
            {/* Tip Buttons */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(3, 1fr)', 
              gap: '8px',
              marginBottom: '12px'
            }}>
              {[0, 2, 5, 10, 15, 20].map((tip) => (
                <button
                  key={tip}
                  type="button"
                  onClick={() => setTipAmount(tip)}
                  style={{
                    padding: '8px 4px',
                    backgroundColor: tipAmount === tip ? '#7C3AED' : '#374151',
                    border: `2px solid ${tipAmount === tip ? '#7C3AED' : '#6B7280'}`,
                    borderRadius: '6px',
                    color: tipAmount === tip ? 'white' : '#D1D5DB',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {tip === 0 ? 'No Tip' : `$${tip}`}
                </button>
              ))}
            </div>
            
            {/* Custom tip slider */}
            <div>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                marginBottom: '8px'
              }}>
                <span style={{ color: '#9CA3AF', fontSize: '12px' }}>Custom:</span>
                <span style={{ 
                  color: '#7C3AED', 
                  fontSize: '14px', 
                  fontWeight: 'bold' 
                }}>
                  ${tipAmount.toFixed(2)}
                </span>
              </div>
              <input
                type="range"
                min="0"
                max={maxTipAmount}
                step="0.50"
                value={tipAmount}
                onChange={(e) => setTipAmount(parseFloat(e.target.value))}
                style={{
                  width: '100%',
                  height: '4px',
                  background: `linear-gradient(to right, #7C3AED 0%, #7C3AED ${(tipAmount/maxTipAmount)*100}%, #374151 ${(tipAmount/maxTipAmount)*100}%, #374151 100%)`,
                  borderRadius: '2px',
                  outline: 'none',
                  cursor: 'pointer',
                  appearance: 'none'
                }}
              />
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                marginTop: '4px'
              }}>
                <span style={{ color: '#6B7280', fontSize: '10px' }}>$0</span>
                <span style={{ color: '#6B7280', fontSize: '10px' }}>${maxTipAmount}</span>
              </div>
            </div>
            
            {/* Tip percentage indicator */}
            {tipAmount > 0 && data.amount && (
              <div style={{
                textAlign: 'center',
                color: '#9CA3AF',
                fontSize: '11px',
                marginTop: '8px'
              }}>
                That's a {((tipAmount / data.amount) * 100).toFixed(0)}% tip!
              </div>
            )}
          </div>

          <div style={{ marginBottom: '32px' }}>
            <label style={{ display: 'block', color: 'white', fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>
              Optional Comment
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Share your thoughts about the performance..."
              rows={3}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#374151',
                border: '2px solid #6B7280',
                borderRadius: '8px',
                color: 'white',
                fontSize: '16px',
                resize: 'vertical',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', justifyContent: 'center' }}>
            <button
              onClick={submitRating}
              disabled={isSubmitting || rating === 0}
              style={{
                ...buttonStyle(rating === 0 ? '#374151' : '#059669', rating === 0 ? '#6B7280' : '#10B981'),
                minWidth: '140px',
                opacity: (isSubmitting || rating === 0) ? 0.5 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              {isSubmitting && (
                <div
                  style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}
                />
              )}
              {isSubmitting ? 'Processing...' : `Submit & Pay $${((data.amount || 0) + tipAmount).toFixed(2)}`}
            </button>
            <button
              onClick={() => data.onShowCancelConfirm?.()}
              disabled={isSubmitting}
              style={{...buttonStyle('#DC2626', '#B91C1C'), minWidth: '140px'}}
            >
              Cancel Payment
            </button>
          </div>

          <div style={{
            marginTop: '24px',
            padding: '16px',
            backgroundColor: '#1E3A8A',
            borderRadius: '12px',
            border: '2px solid #3B82F6'
          }}>
            <p style={{ color: '#BFDBFE', fontSize: '14px', margin: 0, textAlign: 'center', lineHeight: '1.4' }}>
              💡 <strong>Your payment is only processed when you submit a rating.</strong> 
              If you're not satisfied, you can cancel with no charge.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

// Enhanced Reviews Section - Simplified
const ReviewsSection = ({ musicianId, totalRatings, averageRating, isExpanded, onToggle }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [error, setError] = useState(null);
  const { isMobile } = useResponsiveStyles();

  useEffect(() => {
    if (!isExpanded) return;
    
    const fetchReviews = async () => {
      try {
        const ratingsRef = collection(db, 'ratings');
        const reviewsQuery = query(
          ratingsRef,
          where('musicianId', '==', musicianId),
          limit(100)
        );
        
        const querySnapshot = await getDocs(reviewsQuery);
        const reviewsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          timestamp: doc.data().timestamp?.toDate() || new Date()
        }));
        
        // Fetch song data if missing
        const reviewsWithSongData = await Promise.all(
          reviewsData.map(async (review) => {
            if (!review.songName && review.songId) {
              try {
                const songDoc = await getDoc(doc(db, "songs", review.songId));
                if (songDoc.exists()) {
                  const songData = songDoc.data();
                  review.songName = songData.name;
                  review.songArtist = songData.artist;
                }
              } catch (error) {
                secureLogger.error("Error fetching song data:", error);
              }
            }
            return review;
          })
        );
        
        const allReviews = reviewsWithSongData
          .sort((a, b) => b.timestamp - a.timestamp)
          .filter(review => review.comment && review.comment.trim().length > 0);
        
        setReviews(allReviews);
      } catch (error) {
        secureLogger.error('Error fetching reviews:', error);
        setError('Failed to load reviews');
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, [musicianId, isExpanded]);

  if (!isExpanded) return null;

  const displayedReviews = showAll ? reviews : reviews.slice(0, 3);

  return (
    <div style={{
      backgroundColor: '#1F2937',
      border: '8px solid #FBBF24',
      borderRadius: '24px',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      overflow: 'hidden',
      margin: '32px auto',
      maxWidth: '1200px',
      animation: 'slideDown 0.3s ease-out'
    }}>
      {/* Header */}
      <div style={{
        padding: isMobile ? '24px' : '32px',
        borderBottom: '4px solid #F59E0B',
        backgroundColor: '#B45309',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div>
          <h2 style={{
            fontSize: isMobile ? '28px' : '36px',
            fontWeight: 'bold',
            color: 'white',
            margin: '0 0 8px 0',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{ fontSize: isMobile ? '32px' : '40px' }}>📝</span>
            Customer Reviews
          </h2>
          <p style={{ color: '#FED7AA', fontSize: isMobile ? '16px' : '18px', margin: 0 }}>
            {totalRatings > 0 ? (
              `${averageRating.toFixed(1)} ⭐ average • ${totalRatings} total ratings • ${reviews.length} written reviews`
            ) : (
              'No reviews yet - be the first to leave one!'
            )}
          </p>
        </div>
        
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {reviews.length > 3 && (
            <button
              onClick={() => setShowAll(!showAll)}
              style={{
                backgroundColor: '#3B82F6',
                color: 'white',
                border: '4px solid #60A5FA',
                borderRadius: '12px',
                padding: '12px 24px',
                cursor: 'pointer',
                fontSize: isMobile ? '14px' : '16px',
                fontWeight: 'bold'
              }}
            >
              {showAll ? 'Show Less' : `Show All ${reviews.length}`}
            </button>
          )}
          
          <button onClick={onToggle} style={{
            backgroundColor: '#DC2626',
            color: 'white',
            border: '4px solid #B91C1C',
            borderRadius: '12px',
            padding: '12px 16px',
            cursor: 'pointer',
            fontSize: isMobile ? '14px' : '16px',
            fontWeight: 'bold'
          }}>
            Close ✕
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: isMobile ? '24px' : '32px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', color: '#9CA3AF', padding: '40px 20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            Loading reviews...
          </div>
        ) : error ? (
          <div style={{ textAlign: 'center', color: '#EF4444', padding: '40px 20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>❌</div>
            {error}
          </div>
        ) : reviews.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#9CA3AF', padding: '60px 20px', fontStyle: 'italic' }}>
            <div style={{ fontSize: '64px', marginBottom: '24px' }}>💭</div>
            <div style={{ fontSize: '24px', marginBottom: '12px', color: 'white' }}>
              No written reviews yet
            </div>
            <div style={{ fontSize: '16px', lineHeight: '1.5' }}>
              {totalRatings > 0 ? (
                `This musician has ${totalRatings} star rating${totalRatings !== 1 ? 's' : ''} but no written comments yet.`
              ) : (
                'Be the first to request a song and leave a review!'
              )}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {displayedReviews.map((review) => (
              <div key={review.id} style={{
                backgroundColor: '#374151',
                borderRadius: '16px',
                padding: isMobile ? '20px' : '24px',
                border: '2px solid #4B5563'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '16px',
                  flexWrap: 'wrap',
                  gap: '12px'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      color: '#F59E0B',
                      fontSize: isMobile ? '18px' : '20px',
                      fontWeight: 'bold',
                      marginBottom: '8px'
                    }}>
                      {formatRating(review.rating)} ({review.rating}/5 stars)
                    </div>
                    <div style={{
                      color: '#9CA3AF',
                      fontSize: isMobile ? '12px' : '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      flexWrap: 'wrap'
                    }}>
                      <span>📅 {formatDate(review.timestamp)}</span>
                      {(review.songName || review.songArtist) && (
                        <span>🎵 "{review.songName || 'Unknown Song'}"
                          {review.songArtist && ` by ${review.songArtist}`}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {review.amount && (
                    <div style={{
                      backgroundColor: '#065F46',
                      color: '#A7F3D0',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      fontSize: isMobile ? '12px' : '14px',
                      fontWeight: 'bold',
                      border: '2px solid #10B981'
                    }}>
                      ${review.amount.toFixed(2)} paid
                    </div>
                  )}
                </div>
                
                <div style={{
                  color: 'white',
                  lineHeight: '1.6',
                  fontSize: isMobile ? '14px' : '16px',
                  fontStyle: 'italic',
                  padding: '16px',
                  backgroundColor: '#1F2937',
                  borderRadius: '12px',
                  border: '2px solid #374151'
                }}>
                  <span style={{ fontSize: '18px', color: '#FBBF24' }}>"</span>
                  {review.comment}
                  <span style={{ fontSize: '18px', color: '#FBBF24' }}>"</span>
                </div>
              </div>
            ))}
            
            {reviews.length > 0 && (
              <div style={{
                backgroundColor: '#111827',
                borderRadius: '12px',
                padding: '20px',
                textAlign: 'center',
                border: '2px solid #374151'
              }}>
                <div style={{ color: '#9CA3AF', fontSize: isMobile ? '14px' : '16px' }}>
                  📊 Showing {displayedReviews.length} of {reviews.length} written review{reviews.length !== 1 ? 's' : ''}
                  {totalRatings > reviews.length && (
                    ` • ${totalRatings - reviews.length} additional star rating${totalRatings - reviews.length !== 1 ? 's' : ''} without comments`
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default function PublicMusicianPage() {
  const { musicianId } = useParams();
  const [songs, setSongs] = useState([]);
  const [musicianData, setMusicianData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSong, setSelectedSong] = useState(null);
  const [modalType, setModalType] = useState(null);
  const [modalData, setModalData] = useState({});
  const [showReviews, setShowReviews] = useState(false);
  const [acceptingRequests, setAcceptingRequests] = useState(true);
  const [pendingRatings, setPendingRatings] = useState([]);
  const [showRecoveryBanner, setShowRecoveryBanner] = useState(false);
  
  // New states for filtering and pagination
  const [filteredSongs, setFilteredSongs] = useState([]);
  const [artists, setArtists] = useState([]);
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [songsPerPage] = useState(20);

  const { isMobile, cardStyle, buttonStyle } = useResponsiveStyles();

  // Modal management
  const openModal = (type, data = {}) => {
    setModalType(type);
    setModalData(data);
  };

  const closeModal = () => {
    setModalType(null);
    setModalData({});
    setSelectedSong(null);
  };

  // Check request status before opening modal
  const handleRequestSong = (song) => {
    if (!acceptingRequests) {
      alert("This musician is not currently accepting song requests. Please try again later.");
      return;
    }
    
    setSelectedSong(song);
    openModal('request', {
      song,
      musicianId,
      musicianName: musicianData?.displayName || "Unknown Musician",
      onPaymentAuthorized: (song, paymentIntentId, amount) => {
        closeModal();
        openModal('rating', {
          song,
          paymentIntentId,
          amount,
          musicianName: musicianData?.displayName || "Unknown Musician",
          onShowCancelConfirm: () => openModal('cancel-confirm', { paymentIntentId, song }),
          onSuccess: () => {
            openModal('success');
            setTimeout(closeModal, 3000);
          }
        });
      }
    });
  };

  // Listen for payment authorization events
  useEffect(() => {
    const handlePaymentAuthorized = (event) => {
      const { song, paymentIntentId, amount } = event.detail;
      closeModal();
      openModal('rating', {
        song,
        paymentIntentId,
        amount,
        musicianName: musicianData?.displayName || "Unknown Musician",
        onShowCancelConfirm: () => openModal('cancel-confirm', { paymentIntentId, song }),
        onSuccess: () => {
          openModal('success');
          setTimeout(closeModal, 3000);
        }
      });
    };

    window.addEventListener('showRatingModal', handlePaymentAuthorized);
    return () => window.removeEventListener('showRatingModal', handlePaymentAuthorized);
  }, [musicianData]);

  // 🔒 SECURITY FIX: Direct Firestore with aggressive security filtering (CORS issues prevent callable functions)
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // 🔒 SECURITY: Direct Firestore access with strict filtering (callable functions have CORS routing issues)
        secureLogger.log("🔒 Fetching musician data with security filtering for:", musicianId);
        
        const musicianDocRef = doc(db, 'musicians', musicianId);
        const musicianDoc = await getDoc(musicianDocRef);
        
        if (musicianDoc.exists()) {
          const fullData = musicianDoc.data();
          secureLogger.log("🔒 Raw Firestore data received (field count):", Object.keys(fullData).length);
          
          // 🚨 CRITICAL SECURITY: Create ONLY safe public data with explicit allow-list
          const safePublicData = {
            // ONLY these fields are allowed - anything sensitive is blocked
            displayName: typeof fullData.displayName === 'string' ? fullData.displayName : "Unknown Musician",
            averageRating: typeof fullData.averageRating === 'number' ? fullData.averageRating : 0,
            totalRatings: typeof fullData.totalRatings === 'number' ? fullData.totalRatings : 0,
            isActive: fullData.isActive !== false,
            acceptingRequests: fullData.acceptingRequests !== false,
            // Optional safe profile fields
            profileImage: fullData.profileImage || null,
            bio: typeof fullData.bio === 'string' ? fullData.bio : null,
            genre: typeof fullData.genre === 'string' ? fullData.genre : null,
            location: typeof fullData.location === 'string' ? fullData.location : null
          };
          
          // 🚨 CRITICAL SECURITY CHECK: Verify NO sensitive fields leaked through
          const allSensitiveFields = [
            'phoneNumber', 'mobileNumber', 'email', 'phone', 'mobile', 'realName', 
            'fullName', 'legalName', 'emailAddress', 'address', 'street', 'city', 
            'postalCode', 'zipCode', 'ssn', 'socialSecurityNumber', 'bankAccount',
            'stripeAccountId', 'paymentMethod', 'creditCard', 'accountNumber'
          ];
          
          const foundSensitive = allSensitiveFields.filter(field => safePublicData.hasOwnProperty(field));
          
          if (foundSensitive.length > 0) {
            secureLogger.error("🚨 CRITICAL SECURITY BREACH: Sensitive fields found in filtered data:", foundSensitive);
            alert("SECURITY ALERT: Sensitive data detected after filtering!");
            setMusicianData({ displayName: "Security Error" });
            return;
          }
          
          // 🚨 SECURITY: Check for sensitive patterns in all values
          for (const [key, value] of Object.entries(safePublicData)) {
            if (typeof value === 'string' && value.length > 0) {
              // Check for email patterns
              if (value.includes('@') && value.includes('.') && value.indexOf('@') < value.lastIndexOf('.')) {
                secureLogger.error("🚨 SECURITY BREACH: Email pattern detected in field:", key);
                alert(`SECURITY ALERT: Email pattern found in field ${key}`);
                return;
              }
              // Check for phone patterns (10+ digits with optional formatting)
              const phonePattern = /(\+?\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/;
              if (phonePattern.test(value)) {
                secureLogger.error("🚨 SECURITY BREACH: Phone pattern detected in field:", key);
                alert(`SECURITY ALERT: Phone pattern found in field ${key}`);
                return;
              }
              // Check for suspicious patterns (consecutive digits that might be sensitive)
              if (/\d{8,}/.test(value.replace(/\s/g, ''))) {
                secureLogger.error("🚨 SECURITY BREACH: Suspicious number pattern in field:", key);
                alert(`SECURITY ALERT: Suspicious number pattern in field ${key}`);
                return;
              }
            }
          }
          
          // 🚨 FINAL SECURITY CHECK: Log exactly what we're exposing
          secureLogger.log("✅ SECURITY APPROVED: Safe public data fields:", Object.keys(safePublicData));
          secureLogger.log("✅ SECURITY APPROVED: Data types:", Object.fromEntries(
            Object.entries(safePublicData).map(([k, v]) => [k, typeof v])
          ));
          
          setMusicianData(safePublicData);
          setAcceptingRequests(safePublicData.acceptingRequests !== false);
          
        } else {
          secureLogger.log("❌ Musician document not found");
          setMusicianData({ displayName: "Musician Not Found" });
        }

        // Fetch songs (this is safe as songs are public data)
        const songsRef = collection(db, "songs");
        const q = query(songsRef, where("musicianId", "==", musicianId));
        const snapshot = await getDocs(q);
        const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setSongs(list);
        setFilteredSongs(list);
        
        // Extract unique artists
        const uniqueArtists = [...new Set(list.map(song => song.artist))].sort();
        setArtists(uniqueArtists);
        
      } catch (err) {
        secureLogger.error("Error loading data:", err);
        setMusicianData({ displayName: "Error loading profile" });
        setSongs([]);
      } finally {
        setLoading(false);
      }
    };

    if (musicianId) {
      fetchData();
    }
  }, [musicianId]);

  // Check for pending ratings in localStorage (session recovery)
  useEffect(() => {
    console.log('🔍 Checking for pending ratings recovery...');
    console.log('Current musicianId:', musicianId);
    
    try {
      const storedPending = localStorage.getItem('pendingRatings');
      console.log('📦 Raw localStorage data:', storedPending);
      
      if (storedPending) {
        let allPending = JSON.parse(storedPending);
        console.log('📋 All pending ratings:', allPending);
        
        // Remove duplicates based on paymentIntentId
        const uniqueAllPending = allPending.filter((rating, index, self) =>
          index === self.findIndex(r => r.paymentIntentId === rating.paymentIntentId)
        );
        
        // If we removed duplicates, update localStorage
        if (uniqueAllPending.length < allPending.length) {
          console.log('🧹 Cleaning up duplicate pending ratings');
          localStorage.setItem('pendingRatings', JSON.stringify(uniqueAllPending));
          allPending = uniqueAllPending;
        }
        
        // Filter for this musician's pending ratings
        const musicianPending = allPending.filter(r => 
          r.musicianId === musicianId && 
          r.status === 'pending_rating'
        );
        console.log('🎵 Filtered for this musician:', musicianPending);
        
        if (musicianPending.length > 0) {
          // Check if ratings are still valid (less than 48 hours old)
          const cutoffTime = new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString();
          const validPending = musicianPending.filter(r => r.timestamp > cutoffTime);
          console.log('⏰ Valid pending (not expired):', validPending);
          
          // Clean up expired ratings from localStorage
          if (validPending.length < musicianPending.length) {
            const nonMusicianPending = allPending.filter(r => r.musicianId !== musicianId);
            const updatedAllPending = [...nonMusicianPending, ...validPending];
            localStorage.setItem('pendingRatings', JSON.stringify(updatedAllPending));
            console.log('🧹 Removed expired ratings from localStorage');
          }
          
          if (validPending.length > 0) {
            setPendingRatings(validPending);
            setShowRecoveryBanner(true);
            console.log(`✅ Found ${validPending.length} pending rating(s) for recovery`);
          } else {
            console.log('❌ All pending ratings have expired');
          }
        } else {
          console.log('❌ No pending ratings for this musician');
        }
      } else {
        console.log('❌ No pending ratings in localStorage');
      }
    } catch (error) {
      console.error('Failed to check localStorage for pending ratings:', error);
    }
  }, [musicianId]);

  // Filter songs by artist
  useEffect(() => {
    if (selectedArtist) {
      const filtered = songs.filter(song => song.artist === selectedArtist);
      setFilteredSongs(filtered);
    } else {
      setFilteredSongs(songs);
    }
    setCurrentPage(1); // Reset to first page when filter changes
  }, [selectedArtist, songs]);

  // Calculate pagination
  const indexOfLastSong = currentPage * songsPerPage;
  const indexOfFirstSong = indexOfLastSong - songsPerPage;
  const currentSongs = filteredSongs.slice(indexOfFirstSong, indexOfLastSong);
  const totalPages = Math.ceil(filteredSongs.length / songsPerPage);

  // Handle artist selection
  const handleArtistSelect = (artist) => {
    setSelectedArtist(artist === selectedArtist ? null : artist);
  };

  // Handle pagination
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Scroll to top of songs section
    document.getElementById('songs-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Handle recovering a pending rating
  const handleRecoverRating = (pendingRating) => {
    console.log('🔄 Recovering pending rating:', pendingRating);
    
    // Open the rating modal with the recovered data
    openModal('rating', {
      song: {
        id: pendingRating.songId,
        name: pendingRating.songName,
        artist: pendingRating.songArtist,
        price: pendingRating.songPrice,
        musicianId: pendingRating.musicianId
      },
      paymentIntentId: pendingRating.paymentIntentId,
      amount: pendingRating.amount,
      onSuccess: () => {
        // Remove this rating from pending after successful submission
        const updatedPending = pendingRatings.filter(r => 
          r.paymentIntentId !== pendingRating.paymentIntentId
        );
        setPendingRatings(updatedPending);
        
        // Update localStorage
        const allPending = JSON.parse(localStorage.getItem('pendingRatings') || '[]');
        const filteredPending = allPending.filter(r => 
          r.paymentIntentId !== pendingRating.paymentIntentId
        );
        localStorage.setItem('pendingRatings', JSON.stringify(filteredPending));
        
        // Hide banner if no more pending ratings
        if (updatedPending.length === 0) {
          setShowRecoveryBanner(false);
        }
      },
      onCancel: (paymentIntentId) => {
        // Remove this rating from pending after cancellation
        const updatedPending = pendingRatings.filter(r => 
          r.paymentIntentId !== paymentIntentId
        );
        setPendingRatings(updatedPending);
        
        // Hide banner if no more pending ratings
        if (updatedPending.length === 0) {
          setShowRecoveryBanner(false);
        }
      },
      onShowCancelConfirm: () => {
        openModal('cancel-confirm', {
          paymentIntentId: pendingRating.paymentIntentId,
          song: {
            id: pendingRating.songId,
            name: pendingRating.songName,
            artist: pendingRating.songArtist,
            musicianId: pendingRating.musicianId
          },
          onCancel: (paymentIntentId) => {
            // Remove this rating from pending after cancellation
            const updatedPending = pendingRatings.filter(r => 
              r.paymentIntentId !== paymentIntentId
            );
            setPendingRatings(updatedPending);
            
            // Hide banner if no more pending ratings  
            if (updatedPending.length === 0) {
              setShowRecoveryBanner(false);
            }
          }
        });
      }
    });
  };

  // Dismiss recovery banner
  const dismissRecoveryBanner = () => {
    setShowRecoveryBanner(false);
  };

  const containerStyle = {
    minHeight: '100vh',
    backgroundColor: '#111827',
    color: 'white',
    fontFamily: 'system-ui, -apple-system, sans-serif'
  };

  const headerStyle = {
    backgroundColor: '#1F2937',
    borderBottom: '8px solid #7C3AED',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
  };

  if (loading) {
    return (
      <div style={{ ...containerStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '80px',
            height: '80px',
            border: '8px solid #7C3AED',
            borderTop: '8px solid #C4B5FD',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 24px'
          }}></div>
          <p style={{ color: '#C4B5FD', fontSize: '20px', fontWeight: 'bold' }}>Loading profile securely...</p>
        </div>
      </div>
    );
  }

  const musicianName = musicianData?.displayName || "Unknown Musician";
  const averageRating = musicianData?.averageRating || 0;
  const totalRatings = musicianData?.totalRatings || 0;

  return (
    <>
      <div style={containerStyle}>
        {/* Session Recovery Banner */}
        {showRecoveryBanner && pendingRatings.length > 0 && (
          <div style={{
            backgroundColor: '#059669',
            background: 'linear-gradient(135deg, #059669 0%, #10B981 100%)',
            padding: isMobile ? '12px' : '16px',
            position: 'sticky',
            top: 0,
            zIndex: 1000,
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
            animation: 'slideDown 0.3s ease-out'
          }}>
            <div style={{
              maxWidth: '1200px',
              margin: '0 auto',
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'stretch' : 'center',
              justifyContent: 'space-between',
              gap: '12px'
            }}>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: isMobile ? '16px' : '18px',
                  fontWeight: 'bold',
                  marginBottom: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span style={{ fontSize: '20px' }}>🎵</span>
                  Welcome back! You have {pendingRatings.length} song{pendingRatings.length > 1 ? 's' : ''} waiting to be rated
                </div>
                <div style={{ fontSize: isMobile ? '13px' : '14px', opacity: 0.95 }}>
                  {pendingRatings.length === 1 ? (
                    `"${pendingRatings[0].songName}" by ${pendingRatings[0].songArtist} - Ready to rate`
                  ) : (
                    `Rate your requested songs to complete payment`
                  )}
                </div>
              </div>
              
              <div style={{
                display: 'flex',
                gap: '8px',
                flexDirection: isMobile ? 'row' : 'row',
                justifyContent: isMobile ? 'stretch' : 'flex-end'
              }}>
                {pendingRatings.length === 1 ? (
                  <button
                    onClick={() => handleRecoverRating(pendingRatings[0])}
                    style={{
                      backgroundColor: 'white',
                      color: '#059669',
                      padding: isMobile ? '8px 16px' : '10px 20px',
                      border: 'none',
                      borderRadius: '6px',
                      fontWeight: 'bold',
                      fontSize: isMobile ? '14px' : '15px',
                      cursor: 'pointer',
                      flex: isMobile ? 1 : 'none',
                      transition: 'transform 0.2s',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                    }}
                    onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                    onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                  >
                    Rate Now
                  </button>
                ) : (
                  pendingRatings.map((rating, index) => (
                    <button
                      key={rating.paymentIntentId}
                      onClick={() => handleRecoverRating(rating)}
                      style={{
                        backgroundColor: 'white',
                        color: '#059669',
                        padding: isMobile ? '6px 10px' : '8px 14px',
                        border: 'none',
                        borderRadius: '4px',
                        fontWeight: 'bold',
                        fontSize: isMobile ? '12px' : '13px',
                        cursor: 'pointer',
                        transition: 'transform 0.2s',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                      }}
                      onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                      onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                    >
                      Song {index + 1}
                    </button>
                  ))
                )}
                
                <button
                  onClick={dismissRecoveryBanner}
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    padding: isMobile ? '8px 16px' : '10px 20px',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: '6px',
                    fontSize: isMobile ? '14px' : '15px',
                    cursor: 'pointer',
                    flex: isMobile && pendingRatings.length === 1 ? 1 : 'none',
                    transition: 'opacity 0.2s'
                  }}
                  onMouseOver={(e) => e.target.style.opacity = '0.8'}
                  onMouseOut={(e) => e.target.style.opacity = '1'}
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}
        
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.5; }
            }
            @keyframes slideDown {
              from { opacity: 0; transform: translateY(-20px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}
        </style>

        {/* Header Section */}
        <div style={headerStyle}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: isMobile ? '24px 16px 32px' : '32px 32px 48px' }}>
            <div style={{ textAlign: 'center' }}>
              {/* Musician Avatar - RequestASong Logo as Default */}
              <div style={{
                width: isMobile ? '120px' : '160px',
                height: isMobile ? '120px' : '160px',
                margin: '0 auto 32px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                border: '8px solid #FF6B35',
                position: 'relative',
                background: 'linear-gradient(135deg, #FF6B35 0%, #FF8A65 100%)',
                overflow: 'hidden'
              }}>
                {/* Use your actual RequestASong logo */}
                <RequestASongLogo size={isMobile ? 80 : 100} symbolOnly={true} />
                
                {/* Live Indicator */}
                <div style={{
                  position: 'absolute',
                  bottom: '-16px',
                  right: '-16px',
                  width: isMobile ? '36px' : '48px',
                  height: isMobile ? '36px' : '48px',
                  backgroundColor: '#10B981',
                  borderRadius: '50%',
                  border: '8px solid white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  animation: 'pulse 2s infinite'
                }}>
                  <span style={{ fontSize: isMobile ? '16px' : '24px' }}>🎵</span>
                </div>
              </div>

              <h1 style={{ 
                fontSize: isMobile ? '36px' : '72px', 
                fontWeight: 'bold', 
                color: 'white', 
                margin: '0 0 24px 0',
                lineHeight: '1.1'
              }}>
                {musicianName}
              </h1>
              
              {/* Rating Display - Clickable */}
              {totalRatings > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '24px', marginBottom: '32px', flexWrap: 'wrap' }}>
                  <div 
                    onClick={() => setShowReviews(!showReviews)}
                    style={{
                      backgroundColor: '#374151',
                      border: '4px solid #FBBF24',
                      borderRadius: '50px',
                      padding: isMobile ? '12px 24px' : '16px 32px',
                      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      transform: showReviews ? 'scale(1.05)' : 'scale(1)'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontSize: isMobile ? '24px' : '32px' }}>{getRatingStars(averageRating)}</span>
                      <span style={{ fontSize: isMobile ? '18px' : '24px', fontWeight: 'bold', color: '#FBBF24' }}>
                        {averageRating.toFixed(1)}
                      </span>
                      <span style={{ color: '#D1D5DB', fontSize: isMobile ? '14px' : '18px' }}>
                        ({totalRatings} reviews {showReviews ? '▲' : '▼'})
                      </span>
                    </div>
                  </div>
                </div>
              )}
              
              <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                <p style={{ fontSize: isMobile ? '18px' : '24px', color: '#C4B5FD', marginBottom: '24px' }}>
                  🎵 Live Performer • Request Your Favourite Songs • Support the Artist With a Tip
                </p>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  gap: isMobile ? '16px' : '48px', 
                  flexWrap: 'wrap', 
                  fontSize: isMobile ? '14px' : '18px' 
                }}>
                  {/* Status indicators with request status */}
                  {[
                    { 
                      label: acceptingRequests ? 'Accepting Requests' : 'Requests Disabled', 
                      color: acceptingRequests ? '#065F46' : '#7F1D1D', 
                      borderColor: acceptingRequests ? '#10B981' : '#EF4444', 
                      pulse: acceptingRequests 
                    },
                    { label: `${songs.length} Songs Available`, color: '#1E3A8A', borderColor: '#3B82F6' },
                    { label: 'Instant Requests', color: '#581C87', borderColor: '#7C3AED' }
                  ].map((status, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      backgroundColor: status.color,
                      border: `4px solid ${status.borderColor}`,
                      borderRadius: '50px',
                      padding: isMobile ? '8px 16px' : '12px 24px'
                    }}>
                      <div style={{ 
                        width: '12px', 
                        height: '12px', 
                        backgroundColor: status.borderColor, 
                        borderRadius: '50%',
                        animation: status.pulse ? 'pulse 2s infinite' : 'none'
                      }}></div>
                      <span style={{ color: 'white', fontWeight: 'bold' }}>{status.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        <ReviewsSection 
          musicianId={musicianId} 
          totalRatings={totalRatings}
          averageRating={averageRating}
          isExpanded={showReviews}
          onToggle={() => setShowReviews(!showReviews)}
        />

        {/* Main Content */}
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: isMobile ? '24px 16px' : '48px 32px' }}>
          {songs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: isMobile ? '40px 0' : '80px 0' }}>
              <div style={{
                ...cardStyle,
                borderColor: '#4B5563',
                padding: isMobile ? '32px 16px' : '64px 32px',
                maxWidth: '600px',
                margin: '0 auto'
              }}>
                <div style={{ fontSize: isMobile ? '64px' : '96px', marginBottom: '24px' }}>🎼</div>
                <h3 style={{ fontSize: isMobile ? '24px' : '32px', fontWeight: 'bold', color: 'white', margin: '0 0 16px 0' }}>
                  No Songs Available
                </h3>
                <p style={{ fontSize: isMobile ? '16px' : '20px', color: '#9CA3AF', lineHeight: '1.5', margin: 0 }}>
                  This musician hasn't added any songs to their repertoire yet. Check back later!
                </p>
              </div>
            </div>
          ) : (
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
              {/* Show banner when requests are disabled */}
              {!acceptingRequests && (
                <div style={{
                  ...cardStyle,
                  borderColor: '#EF4444',
                  marginBottom: '32px'
                }}>
                  <div style={{
                    padding: isMobile ? '24px' : '32px',
                    backgroundColor: '#7F1D1D',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: isMobile ? '48px' : '64px', marginBottom: '16px' }}>🚫</div>
                    <h3 style={{ 
                      fontSize: isMobile ? '24px' : '32px', 
                      fontWeight: 'bold', 
                      color: 'white', 
                      margin: '0 0 12px 0' 
                    }}>
                      Requests Currently Disabled
                    </h3>
                    <p style={{ 
                      fontSize: isMobile ? '16px' : '20px', 
                      color: '#FCA5A5', 
                      lineHeight: '1.5', 
                      margin: 0 
                    }}>
                      {musicianName} is not accepting song requests right now. Please check back later!
                    </p>
                  </div>
                </div>
              )}

              <div style={cardStyle} id="songs-section">
                <div style={{ 
                  padding: isMobile ? '24px' : '32px', 
                  borderBottom: '4px solid #6D28D9', 
                  backgroundColor: acceptingRequests ? '#581C87' : '#6B7280'
                }}>
                  <h2 style={{ 
                    fontSize: isMobile ? '28px' : '36px', 
                    fontWeight: 'bold', 
                    color: 'white', 
                    display: 'flex', 
                    alignItems: 'center', 
                    margin: 0,
                    gap: '12px'
                  }}>
                    <span style={{ fontSize: isMobile ? '32px' : '40px' }}>
                      {acceptingRequests ? '🎵' : '🚫'}
                    </span>
                    Available Songs ({filteredSongs.length})
                  </h2>
                  <p style={{ 
                    color: acceptingRequests ? '#C4B5FD' : '#D1D5DB', 
                    marginTop: '12px', 
                    fontSize: isMobile ? '16px' : '20px', 
                    margin: '12px 0 0 0' 
                  }}>
                    {acceptingRequests ? 
                      (selectedArtist ? `Showing songs by ${selectedArtist}` : 'Choose a song to request') : 
                      'Requests are currently disabled'}
                  </p>
                </div>
                
                {/* Artist Filter Section */}
                {artists.length > 1 && (
                  <div style={{ 
                    padding: isMobile ? '16px' : '24px',
                    borderBottom: '2px solid #4B5563',
                    backgroundColor: '#1F2937'
                  }}>
                    <div style={{ marginBottom: '12px' }}>
                      <h3 style={{ 
                        color: '#C4B5FD', 
                        fontSize: isMobile ? '16px' : '18px',
                        fontWeight: 'bold',
                        margin: '0 0 12px 0'
                      }}>
                        Filter by Artist:
                      </h3>
                      <div style={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: '8px'
                      }}>
                        <button
                          onClick={() => handleArtistSelect(null)}
                          style={{
                            padding: '8px 16px',
                            borderRadius: '20px',
                            border: '2px solid',
                            borderColor: !selectedArtist ? '#7C3AED' : '#4B5563',
                            backgroundColor: !selectedArtist ? '#7C3AED' : 'transparent',
                            color: 'white',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: selectedArtist ? 'normal' : 'bold',
                            transition: 'all 0.2s'
                          }}
                        >
                          All Artists ({songs.length})
                        </button>
                        {artists.map(artist => {
                          const artistSongCount = songs.filter(s => s.artist === artist).length;
                          return (
                            <button
                              key={artist}
                              onClick={() => handleArtistSelect(artist)}
                              style={{
                                padding: '8px 16px',
                                borderRadius: '20px',
                                border: '2px solid',
                                borderColor: selectedArtist === artist ? '#7C3AED' : '#4B5563',
                                backgroundColor: selectedArtist === artist ? '#7C3AED' : 'transparent',
                                color: 'white',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: selectedArtist === artist ? 'bold' : 'normal',
                                transition: 'all 0.2s'
                              }}
                            >
                              {artist} ({artistSongCount})
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
                
                <div style={{ padding: isMobile ? '16px' : '32px' }}>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))', 
                    gap: isMobile ? '16px' : '24px' 
                  }}>
                    {currentSongs.map((song, index) => (
                      <div 
                        key={song.id} 
                        style={{
                          backgroundColor: acceptingRequests ? '#374151' : '#4B5563',
                          border: `4px solid ${acceptingRequests ? '#4B5563' : '#6B7280'}`,
                          borderRadius: '16px',
                          padding: isMobile ? '16px' : '24px',
                          transition: 'all 0.3s ease',
                          cursor: acceptingRequests ? 'pointer' : 'not-allowed',
                          opacity: acceptingRequests ? 1 : 0.6
                        }}
                        onMouseOver={(e) => {
                          if (acceptingRequests) {
                            e.currentTarget.style.backgroundColor = '#4B5563';
                            e.currentTarget.style.borderColor = '#7C3AED';
                            e.currentTarget.style.transform = 'scale(1.02)';
                          }
                        }}
                        onMouseOut={(e) => {
                          if (acceptingRequests) {
                            e.currentTarget.style.backgroundColor = '#374151';
                            e.currentTarget.style.borderColor = '#4B5563';
                            e.currentTarget.style.transform = 'scale(1)';
                          }
                        }}
                      >
                        <div style={{ marginBottom: '16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <div style={{ 
                              width: '12px', 
                              height: '12px', 
                              backgroundColor: acceptingRequests ? '#7C3AED' : '#6B7280', 
                              borderRadius: '50%' 
                            }}></div>
                            <span style={{ 
                              fontSize: '10px', 
                              color: acceptingRequests ? '#C4B5FD' : '#9CA3AF', 
                              fontWeight: 'bold', 
                              textTransform: 'uppercase', 
                              letterSpacing: '0.1em' 
                            }}>
                              Track #{index + 1}
                            </span>
                          </div>
                          <h3 style={{ 
                            fontWeight: 'bold', 
                            color: acceptingRequests ? 'white' : '#D1D5DB', 
                            fontSize: isMobile ? '18px' : '22px', 
                            margin: '0 0 8px 0' 
                          }}>
                            {song.name}
                          </h3>
                          <p style={{ 
                            color: acceptingRequests ? '#C4B5FD' : '#9CA3AF', 
                            fontSize: isMobile ? '16px' : '18px', 
                            margin: 0 
                          }}>
                            by {song.artist}
                          </p>
                        </div>
                        
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          gap: '12px'
                        }}>
                          <div style={{
                            backgroundColor: acceptingRequests ? '#059669' : '#6B7280',
                            border: `4px solid ${acceptingRequests ? '#10B981' : '#9CA3AF'}`,
                            color: 'white',
                            fontWeight: 'bold',
                            padding: '12px 16px',
                            borderRadius: '12px',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                          }}>
                            <span style={{ fontSize: isMobile ? '16px' : '18px' }}>
                              ${song.price?.toFixed(2) || "0.00"}
                            </span>
                          </div>
                          <button 
                            onClick={() => handleRequestSong(song)}
                            disabled={!acceptingRequests}
                            style={{
                              ...buttonStyle(
                                acceptingRequests ? '#7C3AED' : '#6B7280', 
                                acceptingRequests ? '#6D28D9' : '#9CA3AF'
                              ),
                              cursor: acceptingRequests ? 'pointer' : 'not-allowed',
                              opacity: acceptingRequests ? 1 : 0.6
                            }}
                            onMouseOver={(e) => {
                              if (acceptingRequests) {
                                e.target.style.backgroundColor = '#6D28D9';
                                e.target.style.transform = 'scale(1.05)';
                              }
                            }}
                            onMouseOut={(e) => {
                              if (acceptingRequests) {
                                e.target.style.backgroundColor = '#7C3AED';
                                e.target.style.transform = 'scale(1)';
                              }
                            }}
                          >
                            {acceptingRequests ? 'Request This' : 'Unavailable'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                    <div style={{ 
                      marginTop: '32px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '8px',
                      flexWrap: 'wrap'
                    }}>
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: currentPage === 1 ? '#374151' : '#7C3AED',
                          color: 'white',
                          border: '2px solid #4B5563',
                          borderRadius: '8px',
                          cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                          opacity: currentPage === 1 ? 0.5 : 1,
                          fontWeight: 'bold',
                          transition: 'all 0.2s'
                        }}
                      >
                        Previous
                      </button>
                      
                      <div style={{ 
                        display: 'flex', 
                        gap: '4px',
                        alignItems: 'center',
                        flexWrap: 'wrap'
                      }}>
                        {[...Array(totalPages)].map((_, index) => {
                          const pageNum = index + 1;
                          // Show first, last, current, and adjacent pages
                          const shouldShow = pageNum === 1 || 
                                           pageNum === totalPages || 
                                           Math.abs(pageNum - currentPage) <= 1;
                          
                          if (!shouldShow && pageNum === 2 && currentPage > 3) {
                            return <span key={pageNum} style={{ color: '#9CA3AF', padding: '0 4px' }}>...</span>;
                          }
                          if (!shouldShow && pageNum === totalPages - 1 && currentPage < totalPages - 2) {
                            return <span key={pageNum} style={{ color: '#9CA3AF', padding: '0 4px' }}>...</span>;
                          }
                          if (!shouldShow) return null;
                          
                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              style={{
                                width: '36px',
                                height: '36px',
                                backgroundColor: currentPage === pageNum ? '#7C3AED' : 'transparent',
                                color: 'white',
                                border: `2px solid ${currentPage === pageNum ? '#7C3AED' : '#4B5563'}`,
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontWeight: currentPage === pageNum ? 'bold' : 'normal',
                                transition: 'all 0.2s'
                              }}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>
                      
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: currentPage === totalPages ? '#374151' : '#7C3AED',
                          color: 'white',
                          border: '2px solid #4B5563',
                          borderRadius: '8px',
                          cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                          opacity: currentPage === totalPages ? 0.5 : 1,
                          fontWeight: 'bold',
                          transition: 'all 0.2s'
                        }}
                      >
                        Next
                      </button>
                    </div>
                  )}
                  
                  {/* Song count info */}
                  <div style={{ 
                    marginTop: '16px',
                    textAlign: 'center',
                    color: '#9CA3AF',
                    fontSize: '14px'
                  }}>
                    Showing {indexOfFirstSong + 1}-{Math.min(indexOfLastSong, filteredSongs.length)} of {filteredSongs.length} songs
                    {selectedArtist && ` by ${selectedArtist}`}
                  </div>
                </div>
              </div>

              {/* How It Works Section - only show if accepting requests */}
              {acceptingRequests && (
                <div style={{ marginTop: '32px', ...cardStyle, borderColor: '#3B82F6' }}>
                  <div style={{ padding: isMobile ? '20px' : '24px' }}>
                    <h3 style={{ 
                      fontSize: isMobile ? '20px' : '24px', 
                      fontWeight: 'bold', 
                      color: '#BFDBFE', 
                      margin: '0 0 20px 0',
                      display: 'flex', 
                      alignItems: 'center',
                      gap: '8px',
                      justifyContent: 'center'
                    }}>
                      <span style={{ fontSize: isMobile ? '24px' : '28px' }}>💡</span>
                      How It Works
                    </h3>
                    <div style={{ 
                      display: 'grid',
                      gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                      gap: '20px'
                    }}>
                      {[
                        { step: "1", text: "Choose a song and submit your request with payment authorization", icon: "🎵" },
                        { step: "2", text: "The musician receives notification and performs your song", icon: "🎤" },
                        { step: "3", text: "Rate the performance - payment only processed if you're satisfied", icon: "⭐" },
                        { step: "4", text: "If unsatisfied, cancel payment at no charge", icon: "❌" }
                      ].map((item, index) => (
                        <div key={index} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                          <div style={{
                            width: '32px',
                            height: '32px',
                            backgroundColor: '#3B82F6',
                            border: '3px solid #60A5FA',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: 'bold',
                            flexShrink: 0,
                            fontSize: '14px'
                          }}>
                            {item.step}
                          </div>
                          <div style={{ flex: 1 }}>
                            <p style={{ 
                              color: '#BFDBFE', 
                              lineHeight: '1.4', 
                              fontSize: isMobile ? '14px' : '16px', 
                              margin: 0 
                            }}>
                              <span style={{ marginRight: '8px', fontSize: isMobile ? '16px' : '18px' }}>{item.icon}</span>
                              {item.text}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Combined Modal */}
      <CombinedModal 
        type={modalType}
        isOpen={!!modalType}
        onClose={closeModal}
        data={modalData}
      />
    </>
  );
}